import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import {
  ProjectFilter,
  ProjectsStatistic,
  useProjectStatisticService
} from 'nekst-api';
import { LoadingAnimation, useFilterContext } from 'ui-builder';
import { useDataLoaderNew } from 'data-loader';

export interface ProjectStatisticContextData {
  isLoading: boolean,
  statistic?: ProjectsStatistic,
  refreshStatistic: () => void,
}

export const COMMISSION_SETTINGS_UPDATED = 'COMMISSION_SETTINGS_UPDATED';

const ProjectStatisticReactContext = React.createContext<ProjectStatisticContextData | undefined>(undefined);

export function useProjectStatisticContextRequired() {
  const contextData = useContext(ProjectStatisticReactContext);

  if (!contextData) {
    throw new Error('ProjectStatisticContextData is not provided');
  }

  return contextData;
}

export function ProjectStatisticContext(
  props: {
    children: ReactElement | ReactElement[],
    noProjectsView?: ReactElement,
  }
) {
  const filterContext = useFilterContext<ProjectFilter>();

  const projectStatisticService = useProjectStatisticService();

  const [hasData, setHasData] = useState(false);

  const {
    data: statistic,
    refreshData: refreshStatistic,
    loading,
  } = useDataLoaderNew(() => projectStatisticService.getStatistic(filterContext.filterValue!), {
    dependencies: [JSON.stringify(filterContext.filterValue!)],
    events: [COMMISSION_SETTINGS_UPDATED]
  });

  const contextValue = useMemo<ProjectStatisticContextData>(() => ({
    isLoading: loading,
    statistic: statistic || undefined,
    refreshStatistic
  }), [loading, JSON.stringify(statistic)]);

  function getTotalNumberOfProperties() {
    if (statistic) {
      return statistic.totalByStatus.active
        + statistic.totalByStatus.closed
        + statistic.totalByStatus.archived;
    } else {
      return 0;
    }
  }

  useEffect(() => {
    if (!loading && getTotalNumberOfProperties() > 0 && !hasData) {
      setHasData(true);
    }

  }, [loading]);

  if (statistic) {
    const filterValue = filterContext.filterValue!;

    const isFilterApplied = filterValue.nameContains
      || !filterValue.types.selectAll
      || filterValue.withPastDueOnly
      || !filterValue.assignedTo.selectAll
      || filterValue.setupAreaId;

    if (hasData || !props.noProjectsView || isFilterApplied) {
      return (
        <ProjectStatisticReactContext.Provider value={contextValue}>
          {props.children}
        </ProjectStatisticReactContext.Provider>
      );
    } else {
      return props.noProjectsView;
    }
  } else {
    return (<LoadingAnimation />);
  }
}
